import './index.styl';
import template from './index.jst';
import template_chart from './template_chart.jst';
import { View } from 'backbone.marionette';
import { User } from '../../../models';
import { Preloader } from '../../../utils';
import Radio from "backbone.radio";
import _ from 'lodash';
import Chart from 'chart.js';

const
	playerComparisonChannel = Radio.channel('player:comparison'),
	coachChannel = Radio.channel('coach');

const CHART_OPTIONS = {
	legend: {
		display: false
	},
	tooltips: {
		enabled: false
	},
	elements: {
		line: {
			tension: 0, // disables bezier curves
		},
		point: {
			radius: 4
		}
	},
	scales: {
		yAxes: [{
			id: 'y-axes',
			gridLines: {
				drawBorder: false,
				drawTicks: false,
				display: false,
				zeroLineWidth: 0,
				zeroLineColor: "transparent"
			},
			ticks: {
				color: '#DDDDDD',
				fontSize: 8,
				beginAtZero: false,
				padding: 20
			}
		}],
		xAxes: [
			{
				id: 'x-axes',
				gridLines: {
					drawBorder: false,
					color: 'rgba(255, 255, 255, 0.5)',
					lineWidth: 1,
					zeroLineWidth: 0,
					zeroLineColor: "transparent"
				},
				ticks: {
					fontSize: 10,
					beginAtZero: false,
					color: '#A3AAB2',
					fontStyle: 'bold'
				}
			}]
	}
};

export class PlayerComparisonChart extends View {
	get template() {
		return this.model.get('is_coach_stats') ? template_chart : template;
	}

	get className() {
		return 'player-comparison-stat-lines-chart';
	}

	ui() {
		return {
			chart: '#history-chart',
			by_year: '#by_year',
			by_stats: '#by_stats',
			coach: '.js-coach-locked'
		}
	}

	events() {
		return {
			'click @ui.coach': 'onClickCoach',
			'change @ui.by_year': 'onChangeYear',
			'change @ui.by_stats': 'onChangeStats',
		}
	}

	initialize() {
		this.model.set({
			has_assistant_coach: User.has_assistant_coach
		});

		this.listenTo(this.model, 'change', this.render);
		this.listenTo(playerComparisonChannel, 'show:data', this.prepareModel);
		this.listenTo(playerComparisonChannel, 'player:add', this.onPlayerAdd);
		this.listenTo(playerComparisonChannel, 'removed:player', this.onPlayerRemove);
		this.listenTo(playerComparisonChannel, 'switch:to:multi:tab', this.switchToMulti);
		this.listenTo(playerComparisonChannel, 'switch:to:h2h:tab', this.switchToh2h);
		this.listenTo(this.options.history, 'request:history:data:success', this.refreshChart);
	}

	async onChangeYear({ currentTarget: { value } }) {
		const
			current_year = (new Date()).getFullYear(),
			selected_year = parseInt(value, 10),
			is_different = !_.eq(current_year, selected_year);

		this.model.set({ selected_year }, { silent: is_different });

		if (is_different) {
			Preloader.show();

			await this.options.history.getByYear(selected_year);
			this.options.history.trigger('request:history:data:success');

			Preloader.hide();
		}
	}

	refreshChart() {
		this.calcChartDataSet()
			.model.trigger('change');
	}

	calcChartDataSet() {
		const
			current_year = (new Date()).getFullYear(),
			selected_year = this.model.get('selected_year'),
			is_equal = _.eq(current_year, selected_year);

		if (is_equal) {
			this.model.calcPlayersCoachStats();

			return this;
		}

		const
			players = this.model.get('compare'),
			ids = _.map(players, ({ id }) => id),
			history_players = _.map(ids, id => {
				const player = this.options.history.get(id);

				if (player) {
					return player.toJSON();
				}
			}).filter(_.identity);

		this.model.calcPlayersCoachStats({
			db: _.keyBy(history_players, 'id')
		});

		return this;
	}

	onChangeStats({ currentTarget: { value } }) {
		this.model.set({ selected_history_stat: value });
	}

	destroyChart() {
		if (this.chart instanceof Chart) {
			this.chart.destroy();
			this.chart = undefined;
		}
	}

	onDestroy() {
		this.destroyChart();
	}

	onDomRefresh() {
		this.destroyChart();

		if (!this.model.get('is_coach_stats')) {
			return this;
		}

		this.calcChartDataSet()
			.createChart();
	}

	createChart() {
		const ctx = this.ui.chart;

		if (ctx.length) {
			this.chart = new Chart(ctx, {
				type: 'line',
				responsive: true,
				data: {
					labels: this.model.getRoundsRange(),
					datasets: this.model.getChartDataSet()
				},
				options: CHART_OPTIONS
			});
		}

		return this;
	}

	switchToMulti() {
		this.model.set({ is_coach_stats: true });
	}

	switchToh2h() {
		this.model.set({ is_coach_stats: false });
	}

	prepareModel({ actual_round_id }) {
		this.model.set({ round_id: actual_round_id }, { silent: true });
	}

	onPlayerAdd({ player }) {
		this.model
			.addPlayer({ player })
			.calcPlayersSimpleStats();

		return this;
	}

	onPlayerRemove({ player }) {
		if (_.isArray(player)) {
			this.model.removePlayers({ players: player });
		}
		else {
			this.model.removePlayer({ player_id: player.id });
		}

		this.model.calcPlayersSimpleStats();

		return this;
	}

	onClickCoach() {
		coachChannel.trigger('show:popup');
	}
}

export default PlayerComparisonChart;